import { ManagementType, PortfolioAssetType } from './type';

export const PORTFOLIO_ASSET_TYPE = {
  国内株式: {
    label: '国内株式',
    color: '#49B9A9',
    type: 'JP_EQ',
    ignoreAt1_20: false,
  },
  '外国株式(先進国+新興国)': {
    label: '外国株式',
    color: '#3FA9F5',
    type: 'G_EQ',
    ignoreAt1_20: false,
  },
  先進国株式: {
    label: '先進国株式',
    color: '#2563EB',
    type: 'DC_EQ',
    ignoreAt1_20: false,
  },
  新興国株式: {
    label: '新興国株式',
    color: '#E77E7E',
    type: 'EM_EQ',
    ignoreAt1_20: false,
  },
  国内債券: {
    label: '国内債券',
    color: '#A3E635',
    type: 'JP_FI',
    ignoreAt1_20: false,
  },
  '外国債券(先進国+新興国)': {
    label: '外国債券',
    color: '#FCD34D',
    type: 'G_FI',
    ignoreAt1_20: false,
  },
  先進国債券: {
    label: '先進国債券',
    color: '#53AA34',
    type: 'DC_FI',
    ignoreAt1_20: false,
  },
  新興国債券: {
    label: '新興国債券',
    color: '#B22F13',
    type: 'EM_FI',
    ignoreAt1_20: false,
  },
  'REIT(国内+外国)': {
    label: 'REIT',
    color: '#FB923C',
    type: 'REIT',
    ignoreAt1_20: false,
  },
  国内REIT: {
    label: '国内REIT',
    color: '#FE5F5F',
    type: 'JP_REIT',
    ignoreAt1_20: false,
  },
  外国REIT: {
    label: '外国REIT',
    color: '#A37070',
    type: 'G_REIT',
    ignoreAt1_20: false,
  },
  その他: {
    label: 'その他',
    color: '#D2DEEF',
    type: 'OTHER',
    ignoreAt1_20: false,
  },
} as const;

export const toPortfolioAssetType = (
  value: string
): PortfolioAssetType | null => {
  return (
    Object.values(PORTFOLIO_ASSET_TYPE).find(type => type.type === value)
      ?.type ?? null
  );
};

export const ignoreAt1_20 = (portfolioType: PortfolioAssetType): boolean => {
  const found = Object.values(PORTFOLIO_ASSET_TYPE).find(
    p => p.type === portfolioType
  );
  if (found === undefined) return false;
  return found.ignoreAt1_20;
};

export const portfolioTypeToString = (
  portfolioAssetType: PortfolioAssetType
): string =>
  Object.values(PORTFOLIO_ASSET_TYPE).find(({ type }) => {
    return portfolioAssetType === type;
  })?.label ?? '';

export const portfolioTypeToBackgroundColor = (
  portfolioAssetType: PortfolioAssetType
): string =>
  Object.values(PORTFOLIO_ASSET_TYPE).find(({ type }) => {
    return portfolioAssetType === type;
  })?.color ?? '';

export const PORTFOLIO_BENCH_MARK = {
  'TOPIX (東証株価指数)': {
    label: 'TOPIX(東証株価指数)',
    type: 'JP_EQ',
  },
  'MSCIコクサイ・インデックス、MSCIエマージング・マーケット指数': {
    label: 'MSCIコクサイ・インデックス、MSCIエマージング・マーケット指数',
    type: 'G_EQ',
  },
  'MSCIコクサイ・インデックス': {
    label: 'MSCIコクサイ・インデックス',
    type: 'DC_EQ',
  },
  'MSCIエマージング・マーケット指数': {
    label: 'MSCIエマージング・マーケット指数',
    type: 'EM_EQ',
  },
  BPI総合インデックス: {
    label: 'BPI総合インデックス',
    type: 'JP_FI',
  },
  'FTSE Non-JPY WGBI JPY、EM USD Aggregate': {
    label: 'FTSE Non-JPY WGBI JPY、EM USD Aggregate',
    type: 'G_FI',
  },
  'FTSE Non-JPY WGBI JPY': {
    label: 'FTSE Non-JPY WGBI JPY',
    type: 'DC_FI',
  },
  'EM USD Aggregate': {
    label: 'EM USD Aggregate',
    type: 'EM_FI',
  },
  '東証REIT指数、S&P Global REIT USD Index': {
    label: '東証REIT指数、S&P Global REIT USD Index',
    type: 'REIT',
  },
  東証REIT指数: {
    label: '東証REIT指数',
    type: 'JP_REIT',
  },
  'S&P Global REIT USD Index': {
    label: 'S&P Global REIT USD Index',
    type: 'G_REIT',
  },
  その他: {
    label: 'その他',
    type: 'OTHER',
  },
} as const;

export const portfolioTypeToBenchMark = (
  portfolioAssetType: PortfolioAssetType
): string =>
  Object.values(PORTFOLIO_BENCH_MARK).find(({ type }) => {
    return portfolioAssetType === type;
  })?.label ?? '';

export const INVESTMENT_TRUST_TYPE = {
  国内投信: {
    label: '投資信託',
    type: 'JP_MF',
  },
  国内ETF: {
    label: '国内ETF',
    type: 'JP_ETF',
  },
  米国ETF: {
    label: '米国ETF',
    type: 'US_ETF',
  },
} as const;

export const getManagementItemInfo = (investmentType: ManagementType) => {
  return managementItemInfo.find(list => investmentType === list.type);
};

export const managementItemInfo = [
  {
    type: 1,
    title: 'アセプラ 5 Type ',
    subTitle: '分散投資',
    titleColor: null,
    explanation: '過去のデータから景気動向を予想したベーシックな分散投資です。',
    imgName: 'asset_default',
    dispType: ['JP_EQ', 'G_EQ', 'JP_FI', 'G_FI', 'REIT', 'OTHER'],
  },
  {
    type: 2,
    title: 'アセプラ 8 Types ',
    subTitle: '分散投資',
    titleColor: 'text-orange-650',
    explanation:
      '過去のデータから景気動向を予想したベーシックな分散投資です。デフォルトのアセットを詳細に表示します。',
    imgName: 'asset_8',
    dispType: [
      'JP_EQ',
      'DC_EQ',
      'EM_EQ',
      'JP_FI',
      'DC_FI',
      'EM_FI',
      'JP_REIT',
      'G_REIT',
      'OTHER',
    ],
  },
  {
    type: 3,
    title: '株式のみ',
    subTitle: 'グローバル分散投資',
    titleColor: 'text-teal-400',
    explanation: '株式のみで新興国、先進国、国内へ分散投資。',
    imgName: 'stock',
    dispType: [
      'JP_EQ',
      'DC_EQ',
      'EM_EQ',
      'JP_FI',
      'DC_FI',
      'EM_FI',
      'JP_REIT',
      'G_REIT',
      'OTHER',
    ],
  },
  {
    type: 4,
    title: '景気拡大',
    subTitle: '予想の分散投資',
    titleColor: 'text-orange-200',
    explanation: '経済が成長すると思う方へおすすめのグローバル分散投資。',
    imgName: 'economic_growth',
    dispType: [
      'JP_EQ',
      'DC_EQ',
      'EM_EQ',
      'JP_FI',
      'DC_FI',
      'EM_FI',
      'JP_REIT',
      'G_REIT',
      'OTHER',
    ],
  },
  {
    type: 5,
    title: '景気縮小',
    subTitle: '予想の分散投資',
    titleColor: 'text-blue-650',
    explanation: '経済が縮小すると思う方へおすすめのグローバル分散投資です。',
    imgName: 'economic_downturn',
    dispType: [
      'JP_EQ',
      'DC_EQ',
      'EM_EQ',
      'JP_FI',
      'DC_FI',
      'EM_FI',
      'JP_REIT',
      'G_REIT',
      'OTHER',
    ],
  },
  {
    type: 6,
    title: '国内強気',
    subTitle: '予想の分散投資',
    titleColor: 'text-rose-600',
    explanation: '国内が成長すると思う方へおすすめのグローバル分散投資。',
    imgName: 'domestic',
    dispType: [
      'JP_EQ',
      'DC_EQ',
      'EM_EQ',
      'JP_FI',
      'DC_FI',
      'EM_FI',
      'JP_REIT',
      'G_REIT',
      'OTHER',
    ],
  },
  {
    type: 7,
    title: '先進国強気',
    subTitle: '予想の分散投資',
    titleColor: 'text-orangered-500',
    explanation: '先進国が成長すると思う方へおすすめのグローバル分散投資。',
    imgName: 'developed',
    dispType: [
      'JP_EQ',
      'DC_EQ',
      'EM_EQ',
      'JP_FI',
      'DC_FI',
      'EM_FI',
      'JP_REIT',
      'G_REIT',
      'OTHER',
    ],
  },
  {
    type: 8,
    title: '新興国強気',
    subTitle: '予想の分散投資',
    titleColor: 'text-rose-400',
    explanation: '新興国が成長すると思う方へおすすめのグローバル分散投資。',
    imgName: 'emerging',
    dispType: [
      'JP_EQ',
      'DC_EQ',
      'EM_EQ',
      'JP_FI',
      'DC_FI',
      'EM_FI',
      'JP_REIT',
      'G_REIT',
      'OTHER',
    ],
  },
];
